import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings as SettingsIcon, Bell, Lock, Globe, User, Building2,
  Printer, CreditCard, ChevronRight, Save, ShieldCheck,
  Coffee, Trash2, Plus, Shield, Edit3,
  Wifi, Zap, Key, DollarSign, Percent, Eye, EyeOff,
  Activity, Fingerprint, FileText, Loader2,
  CheckCircle, AlertTriangle, Receipt, Smartphone,
  ToggleLeft, ToggleRight, RefreshCw, Ban, Info
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/setting.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';

/* ─────────────────────────────────────────────
   API
───────────────────────────────────────────── */
const api = () => {
  const t = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${t}` } });
};

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const GST_RATES = [0, 5, 12, 18, 28];

const NAV = [
  { id: 'profile',  label: 'Restaurant Profile',  icon: <Building2 size={15}/> },
  { id: 'gst',      label: 'GST & Billing',        icon: <Receipt size={15}/> },
  { id: 'orders',   label: 'Order Settings',        icon: <Coffee size={15}/> },
  { id: 'password', label: 'Change Password',       icon: <Lock size={15}/> },
  { id: 'danger',   label: 'Danger Zone',           icon: <Shield size={15}/>, danger: true },
];

/* ─────────────────────────────────────────────
   TOAST HOOK
───────────────────────────────────────────── */
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
}

/* ─────────────────────────────────────────────
   TOGGLE SWITCH
───────────────────────────────────────────── */
function Toggle({ checked, onChange }) {
  return (
    <label className="st-switch" onClick={e => e.stopPropagation()}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}/>
      <span className="st-switch-track"/>
    </label>
  );
}

/* ─────────────────────────────────────────────
   SECTION: RESTAURANT PROFILE
───────────────────────────────────────────── */
function ProfileSection({ show }) {
  const { toast, show: fire } = useToast();
  const [form,    setForm]    = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!show) return;
    api().get('/settings/profile')
      .then(r => setForm(r.data.restaurant || {}))
      .catch(() => fire('Failed to load profile', 'error'))
      .finally(() => setLoading(false));
  }, [show]); // eslint-disable-line

  const patch = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api().put('/settings/profile', form);
      // Update local storage name if changed
      if (form.name) localStorage.setItem('restaurant_name', form.name);
      fire('Profile saved successfully');
    } catch (e) {
      fire(e.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="st-body"><div style={{ display:'flex', justifyContent:'center', padding: 60 }}><Loader2 size={24} className="st-spin" style={{ color:'var(--tomato)' }}/></div></div>;

  return (
    <div className="st-body st-animate">
      {/* Business Identity */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Building2 size={14}/> Business Identity</div>
            <div className="st-group-sub">Basic information shown on bills and QR menus</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-field-grid">
            <div className="st-field">
              <label className="st-label">Restaurant Name *</label>
              <input className="st-input" value={form.name || ''} onChange={e => patch('name', e.target.value)} placeholder="e.g. The Gourmet Kitchen"/>
            </div>
            <div className="st-field">
              <label className="st-label">Tagline</label>
              <input className="st-input" value={form.tagline || ''} onChange={e => patch('tagline', e.target.value)} placeholder="e.g. Fine dining, casual feel"/>
            </div>
            <div className="st-field">
              <label className="st-label">Phone</label>
              <input className="st-input" value={form.phone || ''} onChange={e => patch('phone', e.target.value)} placeholder="+91 98765 43210"/>
            </div>
            <div className="st-field">
              <label className="st-label">Email</label>
              <input className="st-input" type="email" value={form.email || ''} onChange={e => patch('email', e.target.value)} placeholder="info@restaurant.com"/>
            </div>
            <div className="st-field">
              <label className="st-label">Restaurant Type</label>
              <select className="st-select" value={form.type || ''} onChange={e => patch('type', e.target.value)}>
                <option value="">Select type</option>
                <option value="restaurant">Restaurant</option>
                <option value="cafe">Café</option>
                <option value="bar">Bar & Grill</option>
                <option value="bakery">Bakery</option>
                <option value="food_court">Food Court</option>
                <option value="cloud_kitchen">Cloud Kitchen</option>
                <option value="food_truck">Food Truck</option>
                <option value="qsr">Quick Service (QSR)</option>
              </select>
            </div>
            <div className="st-field">
              <label className="st-label">Logo URL</label>
              <input className="st-input" value={form.logo_url || ''} onChange={e => patch('logo_url', e.target.value)} placeholder="https://..."/>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Identity */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><FileText size={14}/> Tax Identity</div>
            <div className="st-group-sub">Shown on invoices and GST reports</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-field-grid">
            <div className="st-field">
              <label className="st-label">GSTIN</label>
              <input className="st-input" value={form.gst_number || ''} onChange={e => patch('gst_number', e.target.value)} placeholder="22AAAAA0000A1Z5" style={{ fontFamily: 'var(--f-mono)' }}/>
            </div>
            <div className="st-field">
              <label className="st-label">PAN Number</label>
              <input className="st-input" value={form.pan_number || ''} onChange={e => patch('pan_number', e.target.value)} placeholder="AAAAA0000A" style={{ fontFamily: 'var(--f-mono)' }}/>
            </div>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Globe size={14}/> Address</div>
            <div className="st-group-sub">Printed on bills and used for delivery zones</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-field-grid">
            <div className="st-field full">
              <label className="st-label">Street Address</label>
              <input className="st-input" value={form.address || ''} onChange={e => patch('address', e.target.value)} placeholder="123 MG Road, Near Central Mall"/>
            </div>
            <div className="st-field">
              <label className="st-label">City</label>
              <input className="st-input" value={form.city || ''} onChange={e => patch('city', e.target.value)} placeholder="Mumbai"/>
            </div>
            <div className="st-field">
              <label className="st-label">State</label>
              <input className="st-input" value={form.state || ''} onChange={e => patch('state', e.target.value)} placeholder="Maharashtra"/>
            </div>
            <div className="st-field">
              <label className="st-label">Pincode</label>
              <input className="st-input" value={form.pincode || ''} onChange={e => patch('pincode', e.target.value)} placeholder="400001" maxLength={6}/>
            </div>
            <div className="st-field">
              <label className="st-label">Country</label>
              <select className="st-select" value={form.country || 'India'} onChange={e => patch('country', e.target.value)}>
                <option value="India">India</option>
                <option value="UAE">UAE</option>
                <option value="UK">UK</option>
                <option value="USA">USA</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bill footer */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Receipt size={14}/> Bill Footer Note</div>
            <div className="st-group-sub">Printed at the bottom of every bill</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-field">
            <label className="st-label">Footer Message</label>
            <textarea className="st-textarea" value={form.footer_note || ''} onChange={e => patch('footer_note', e.target.value)} placeholder="Thank you for dining with us! Visit again soon."/>
          </div>
        </div>
      </div>

      {/* Save */}
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="st-btn primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="st-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>

      {toast && (
        <div className={`st-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION: GST & BILLING
───────────────────────────────────────────── */
function GstSection({ show }) {
  const { toast, show: fire } = useToast();
  const [form,    setForm]    = useState({ default_rate: 5, default_type: 'exclusive', show_on_bill: true });
  const [misc,    setMisc]    = useState({ billing: { currency: 'INR', print_after_place: false } });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!show) return;
    Promise.all([
      api().get('/settings/gst'),
      api().get('/settings/misc'),
    ]).then(([g, m]) => {
      setForm(g.data.gst || { default_rate: 5, default_type: 'exclusive', show_on_bill: true });
      setMisc(m.data.settings || {});
    }).catch(() => fire('Failed to load GST settings', 'error'))
      .finally(() => setLoading(false));
  }, [show]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api().put('/settings/gst', form),
        api().put('/settings/misc', { billing: misc.billing || {} }),
      ]);
      fire('GST & billing settings saved');
    } catch (e) {
      fire(e.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="st-body"><div style={{ display:'flex', justifyContent:'center', padding: 60 }}><Loader2 size={24} className="st-spin" style={{ color:'var(--tomato)' }}/></div></div>;

  const billing = misc.billing || {};

  return (
    <div className="st-body st-animate">
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Receipt size={14}/> Default GST Rate</div>
            <div className="st-group-sub">Applied to all new orders unless overridden at billing</div>
          </div>
        </div>
        <div className="st-group-body">
          <div style={{ display:'flex', flexDirection:'column', gap: 16 }}>
            {/* Rate selector */}
            <div className="st-field">
              <label className="st-label">Default Rate</label>
              <div className="st-rate-pills">
                {GST_RATES.map(r => (
                  <button key={r}
                    className={`st-rate-pill ${form.default_rate === r ? 'active' : ''}`}
                    onClick={() => setForm(p => ({ ...p, default_rate: r }))}>
                    {r}%
                  </button>
                ))}
              </div>
            </div>

            {/* Type selector */}
            <div className="st-field">
              <label className="st-label">Tax Type</label>
              <div className="st-type-pills" style={{ maxWidth: 280 }}>
                {[
                  { id: 'exclusive', label: 'Exclusive (added on top)' },
                  { id: 'inclusive', label: 'Inclusive (within price)' },
                ].map(t => (
                  <button key={t.id}
                    className={`st-type-btn ${form.default_type === t.id ? 'active' : ''}`}
                    onClick={() => setForm(p => ({ ...p, default_type: t.id }))}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Show on bill toggle */}
            <div className="st-toggle-row" style={{ paddingTop: 0, paddingBottom: 0, border: 'none' }}>
              <div className="st-toggle-icon"><FileText size={15}/></div>
              <div className="st-toggle-text">
                <span className="st-toggle-label">Show GST on Bill</span>
                <span className="st-toggle-desc">Print GST breakdown on customer receipts</span>
              </div>
              <Toggle checked={!!form.show_on_bill} onChange={v => setForm(p => ({ ...p, show_on_bill: v }))}/>
            </div>
          </div>
        </div>
      </div>

      {/* Billing preferences */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><DollarSign size={14}/> Billing Preferences</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-toggle-row">
            <div className="st-toggle-icon"><Printer size={15}/></div>
            <div className="st-toggle-text">
              <span className="st-toggle-label">Auto-print after placing order</span>
              <span className="st-toggle-desc">Automatically open print dialog when an order is confirmed</span>
            </div>
            <Toggle checked={!!billing.print_after_place}
              onChange={v => setMisc(p => ({ ...p, billing: { ...p.billing, print_after_place: v } }))}/>
          </div>
          <div className="st-toggle-row">
            <div className="st-toggle-icon"><Receipt size={15}/></div>
            <div className="st-toggle-text">
              <span className="st-toggle-label">Currency</span>
              <span className="st-toggle-desc">Display currency for all bills and reports</span>
            </div>
            <select className="st-select" style={{ width: 100 }}
              value={billing.currency || 'INR'}
              onChange={e => setMisc(p => ({ ...p, billing: { ...p.billing, currency: e.target.value } }))}>
              <option value="INR">₹ INR</option>
              <option value="USD">$ USD</option>
              <option value="AED">AED</option>
              <option value="GBP">£ GBP</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="st-btn primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="st-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save GST Settings'}
        </button>
      </div>

      {toast && (
        <div className={`st-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION: ORDER SETTINGS
───────────────────────────────────────────── */
function OrderSection({ show }) {
  const { toast, show: fire } = useToast();
  const [settings, setSettings] = useState({ order: {}, notifications: {} });
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!show) return;
    api().get('/settings/misc')
      .then(r => setSettings(r.data.settings || {}))
      .catch(() => fire('Failed to load', 'error'))
      .finally(() => setLoading(false));
  }, [show]); // eslint-disable-line

  const patchOrder = (k, v) =>
    setSettings(p => ({ ...p, order: { ...(p.order || {}), [k]: v } }));

  const patchNotif = (k, v) =>
    setSettings(p => ({ ...p, notifications: { ...(p.notifications || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api().put('/settings/misc', {
        order: settings.order || {},
        notifications: settings.notifications || {},
      });
      fire('Order settings saved');
    } catch (e) {
      fire(e.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="st-body"><div style={{ display:'flex', justifyContent:'center', padding: 60 }}><Loader2 size={24} className="st-spin" style={{ color:'var(--tomato)' }}/></div></div>;

  const o = settings.order        || {};
  const n = settings.notifications || {};

  return (
    <div className="st-body st-animate">
      {/* Order behaviour */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Coffee size={14}/> Order Behaviour</div>
            <div className="st-group-sub">Controls how orders are created and confirmed</div>
          </div>
        </div>
        <div className="st-group-body">
          {[
            {
              icon: <User size={15}/>, key: 'require_customer_name',
              label: 'Require Customer Name',
              desc: 'Block order placement if customer name is not filled',
            },
            {
              icon: <Smartphone size={15}/>, key: 'require_customer_phone',
              label: 'Require Customer Phone',
              desc: 'Block order placement if customer phone is not filled',
            },
            {
              icon: <Zap size={15}/>, key: 'auto_kot',
              label: 'Auto-send KOT',
              desc: 'Automatically send Kitchen Order Ticket when order is placed',
            },
            {
              icon: <RefreshCw size={15}/>, key: 'allow_edit_after_place',
              label: 'Allow Edit After Placing',
              desc: 'Staff can add/remove items on already-placed orders',
            },
          ].map((t, i) => (
            <div key={i} className="st-toggle-row">
              <div className="st-toggle-icon">{t.icon}</div>
              <div className="st-toggle-text">
                <span className="st-toggle-label">{t.label}</span>
                <span className="st-toggle-desc">{t.desc}</span>
              </div>
              <Toggle checked={!!o[t.key]} onChange={v => patchOrder(t.key, v)}/>
            </div>
          ))}
        </div>
      </div>

      {/* QR ordering */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Smartphone size={14}/> QR Ordering</div>
          </div>
        </div>
        <div className="st-group-body">
          {[
            {
              icon: <Wifi size={15}/>, key: 'qr_ordering_enabled',
              label: 'Enable QR Ordering',
              desc: 'Allow customers to place orders by scanning the table QR code',
            },
            {
              icon: <ShieldCheck size={15}/>, key: 'qr_require_confirmation',
              label: 'Require Staff Confirmation',
              desc: 'Hold QR orders for staff approval before sending to kitchen',
            },
          ].map((t, i) => (
            <div key={i} className="st-toggle-row">
              <div className="st-toggle-icon">{t.icon}</div>
              <div className="st-toggle-text">
                <span className="st-toggle-label">{t.label}</span>
                <span className="st-toggle-desc">{t.desc}</span>
              </div>
              <Toggle checked={!!o[t.key]} onChange={v => patchOrder(t.key, v)}/>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications */}
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Bell size={14}/> POS Notifications</div>
          </div>
        </div>
        <div className="st-group-body">
          {[
            {
              icon: <Bell size={15}/>, key: 'new_order_sound',
              label: 'New Order Alert Sound',
              desc: 'Play a sound when a new order arrives (including QR orders)',
            },
            {
              icon: <Activity size={15}/>, key: 'idle_refresh_enabled',
              label: 'Auto-Refresh When Idle',
              desc: 'Refresh tables and orders when POS is idle for 30 seconds',
            },
          ].map((t, i) => (
            <div key={i} className="st-toggle-row">
              <div className="st-toggle-icon">{t.icon}</div>
              <div className="st-toggle-text">
                <span className="st-toggle-label">{t.label}</span>
                <span className="st-toggle-desc">{t.desc}</span>
              </div>
              <Toggle checked={!!n[t.key]} onChange={v => patchNotif(t.key, v)}/>
            </div>
          ))}

          {/* Idle refresh seconds */}
          <div className="st-toggle-row" style={{ border: 'none', paddingBottom: 0 }}>
            <div className="st-toggle-icon"><RefreshCw size={15}/></div>
            <div className="st-toggle-text">
              <span className="st-toggle-label">Idle Timeout (seconds)</span>
              <span className="st-toggle-desc">How long before the idle refresh triggers</span>
            </div>
            <input
              type="number" min={10} max={300}
              value={n.idle_refresh_sec || 30}
              onChange={e => patchNotif('idle_refresh_sec', parseInt(e.target.value) || 30)}
              className="st-input"
              style={{ width: 80, textAlign: 'center', fontFamily: 'var(--f-mono)' }}
            />
          </div>
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="st-btn primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="st-spin"/> : <Save size={14}/>}
          {saving ? 'Saving…' : 'Save Order Settings'}
        </button>
      </div>

      {toast && (
        <div className={`st-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION: CHANGE PASSWORD
───────────────────────────────────────────── */
function PasswordSection({ show }) {
  const { toast, show: fire } = useToast();
  const [form,   setForm]   = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show1,  setShow1]  = useState(false);
  const [show2,  setShow2]  = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.current_password || !form.new_password)
      return fire('All fields are required', 'error');
    if (form.new_password.length < 8)
      return fire('New password must be at least 8 characters', 'error');
    if (form.new_password !== form.confirm_password)
      return fire('New passwords do not match', 'error');

    setSaving(true);
    try {
      await api().put('/settings/password', {
        current_password: form.current_password,
        new_password:     form.new_password,
      });
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      fire('Password changed successfully');
    } catch (e) {
      fire(e.response?.data?.message || 'Failed to change password', 'error');
    } finally { setSaving(false); }
  };

  const p = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="st-body st-animate">
      <div className="st-group">
        <div className="st-group-header">
          <div>
            <div className="st-group-title"><Lock size={14}/> Change Password</div>
            <div className="st-group-sub">Use a strong password of at least 8 characters</div>
          </div>
        </div>
        <div className="st-group-body">
          <div className="st-field">
            <label className="st-label">Current Password</label>
            <div className="st-input-icon">
              <input type={show1 ? 'text' : 'password'} value={form.current_password}
                onChange={e => p('current_password', e.target.value)}
                placeholder="Enter current password"/>
              <button className="st-input-icon-btn" onClick={() => setShow1(v => !v)}>
                {show1 ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
          </div>

          <div className="st-field-grid">
            <div className="st-field">
              <label className="st-label">New Password</label>
              <div className="st-input-icon">
                <input type={show2 ? 'text' : 'password'} value={form.new_password}
                  onChange={e => p('new_password', e.target.value)}
                  placeholder="At least 8 characters"/>
                <button className="st-input-icon-btn" onClick={() => setShow2(v => !v)}>
                  {show2 ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </div>
            <div className="st-field">
              <label className="st-label">Confirm New Password</label>
              <div className="st-input-icon">
                <input type="password" value={form.confirm_password}
                  onChange={e => p('confirm_password', e.target.value)}
                  placeholder="Repeat new password"/>
                <span style={{ width: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {form.confirm_password && (
                    form.new_password === form.confirm_password
                      ? <CheckCircle size={14} style={{ color: 'var(--mint)' }}/>
                      : <AlertTriangle size={14} style={{ color: 'var(--rose)' }}/>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Strength indicator */}
          {form.new_password && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600 }}>
                Password strength
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[8, 12, 16].map((len, i) => (
                  <div key={i} style={{
                    flex: 1, height: 4, borderRadius: 2,
                    background: form.new_password.length > len
                      ? (i === 0 ? 'var(--rose)' : i === 1 ? 'var(--saffron)' : 'var(--mint)')
                      : 'var(--border-2)',
                    transition: 'background 0.25s',
                  }}/>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>
                {form.new_password.length < 8 ? 'Too short'
                  : form.new_password.length < 12 ? 'Weak — add more characters'
                  : form.new_password.length < 16 ? 'Good'
                  : 'Strong ✓'}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="st-btn primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="st-spin"/> : <Key size={14}/>}
          {saving ? 'Changing…' : 'Change Password'}
        </button>
      </div>

      {toast && (
        <div className={`st-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION: DANGER ZONE
───────────────────────────────────────────── */
function DangerSection() {
  const { toast, show: fire } = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [deleting,    setDeleting]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE')
      return fire('Type DELETE to confirm', 'error');
    setDeleting(true);
    try {
      await api().delete('/settings/account', { data: { confirm_text: 'DELETE' } });
      fire('Account deactivated. Logging out…');
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 2000);
    } catch (e) {
      fire(e.response?.data?.message || 'Failed', 'error');
    } finally { setDeleting(false); }
  };

  return (
    <div className="st-body st-animate">
      <div className="st-danger-zone">
        <div className="st-danger-header">
          <Shield size={15}/> Danger Zone
        </div>
        <div className="st-danger-body">
          <div className="st-danger-item">
            <div>
              <div className="st-danger-label">Deactivate Account</div>
              <div className="st-danger-desc">
                Permanently deactivate this restaurant account. All data is retained but
                the account will be locked. Contact support to reactivate.
              </div>
            </div>
            <button className="st-btn danger sm"
              style={{ flexShrink: 0 }}
              onClick={() => setShowConfirm(v => !v)}>
              <Ban size={13}/> Deactivate
            </button>
          </div>

          {showConfirm && (
            <div style={{
              background: 'rgba(255,45,85,0.05)',
              border: '1px solid rgba(255,45,85,0.18)',
              borderRadius: 'var(--r-sm)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, color:'var(--rose)', fontSize:12, fontWeight:700 }}>
                <AlertTriangle size={14}/> This action is irreversible. Type DELETE to confirm.
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input className="st-input" placeholder='Type "DELETE"'
                  value={confirmText} onChange={e => setConfirmText(e.target.value)}
                  style={{ fontFamily:'var(--f-mono)', letterSpacing:1 }}/>
                <button className="st-btn danger" onClick={handleDelete} disabled={deleting || confirmText !== 'DELETE'}>
                  {deleting ? <Loader2 size={14} className="st-spin"/> : <Trash2 size={14}/>}
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className={`st-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15}/> : <AlertTriangle size={15}/>}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */
export default function Settings() {
  const [active, setActive] = useState('profile');

  const SECTION_MAP = {
    profile:  <ProfileSection  show={active === 'profile'}  />,
    gst:      <GstSection      show={active === 'gst'}      />,
    orders:   <OrderSection    show={active === 'orders'}   />,
    password: <PasswordSection show={active === 'password'} />,
    danger:   <DangerSection />,
  };

  const current = NAV.find(n => n.id === active);

  return (
    <div className="st-root">
      <Navbar />
      <div className="st-main">

        {/* Left nav */}
        <nav className="st-nav">
          <div className="st-nav-brand">
            <div className="st-nav-brand-title">Settings</div>
            <div className="st-nav-brand-sub">Restaurant Config</div>
          </div>

          <div className="st-nav-section">Configure</div>
          {NAV.filter(n => !n.danger).map(n => (
            <button key={n.id}
              className={`st-nav-btn ${active === n.id ? 'active' : ''}`}
              onClick={() => setActive(n.id)}>
              {n.icon}
              <span>{n.label}</span>
            </button>
          ))}

          <div className="st-nav-section" style={{ marginTop: 'auto', paddingTop: 12 }}>Danger</div>
          {NAV.filter(n => n.danger).map(n => (
            <button key={n.id}
              className={`st-nav-btn danger ${active === n.id ? 'active' : ''}`}
              onClick={() => setActive(n.id)}>
              {n.icon}
              <span>{n.label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="st-content">
          {/* Header */}
          <div className="st-header">
            <div className="st-header-left">
              <div className="st-header-eyebrow">Settings</div>
              <div className="st-header-title">{current?.label}</div>
              <div className="st-header-sub">Manage your restaurant configuration</div>
            </div>
          </div>

          {/* Section content */}
          {SECTION_MAP[active] || null}
        </div>
      </div>
    </div>
  );
}
